import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { IoIosKey } from "react-icons/io";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import ButtonLoader from "../../components/common/ButtonLoader";
import axiosInstance from "../../utils/axios";

const Login = () => {
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({ phoneNumber: "", password: "" });
  const [isLoading, setIsLoading] = useState(false);

  // 🔹 Load saved credentials only if they exist
  useEffect(() => {
    const savedCreds = localStorage.getItem("login-credentials");
    if (savedCreds) {
      const { phoneNumber, password } = JSON.parse(savedCreds);
      setPhoneNumber(phoneNumber || "");
      setPassword(password || "");
      setRememberMe(true);
    }
  }, []);

  const validateForm = () => {
    let valid = true;
    const newErrors = { phoneNumber: "", password: "" };

    if (!phoneNumber) {
      newErrors.phoneNumber = "Phone number is required.";
      valid = false;
    } else if (!/^\d{10}$/.test(phoneNumber)) {
      newErrors.phoneNumber = "Phone number must be 10 digits.";
      valid = false;
    }

    if (!password) {
      newErrors.password = "Password is required.";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleLogin = async () => {
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const resp = await axiosInstance.post("admins/admin-login", {
        phoneNumber,
        password,
        userType: "admin",
      });

      if (resp.data?.token) {
        localStorage.setItem("manika-token", resp.data.token);
        localStorage.setItem("mankia-adminData", JSON.stringify(resp.data.admin));

        if (rememberMe) {
          localStorage.setItem(
            "login-credentials",
            JSON.stringify({ phoneNumber, password })
          );
        } else {
          localStorage.removeItem("login-credentials");
        }

        navigate("/dashboard");
      } else {
        setErrors((prev) => ({
          ...prev,
          password: "Invalid credentials. Please try again.",
          phoneNumber: "Invalid credentials. Please try again.",
        }));
      }
    } catch (error) {
      console.error("Login error:", error);
      setErrors((prev) => ({
        ...prev,
        password: error.response?.data?.message || "Invalid credentials. Please try again.",
        phoneNumber: error.response?.data?.message || "Invalid credentials. Please try again.",
      }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-tr from-slate-300 to-slate-100 flex items-center justify-center px-4">
      <div className="w-full max-w-lg bg-white rounded-xl shadow-xl p-10">
        <h1 className="text-center text-2xl font-bold text-primaryColor mb-1">
          Welcome Back!
        </h1>
        <p className="text-center text-sm text-gray-500 mb-6">
          Enter your credentials to continue
        </p>

        <div className="space-y-5">
          {/* Phone Number Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Phone Number
            </label>
            <input
              type="text"
              value={phoneNumber}
              maxLength={10}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className={`mt-1 block w-full px-4 py-2 border ${errors.phoneNumber ? "border-red-500" : "border-gray-300"
                } rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primaryColor`}
              placeholder="Enter 10 digit phone number"
            />
            {errors.phoneNumber && (
              <p className="text-xs text-red-500 mt-1">{errors.phoneNumber}</p>
            )}
          </div>

          {/* Password Field with Toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`mt-1 block w-full px-4 py-2 border ${errors.password ? "border-red-500" : "border-gray-300"
                  } rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primaryColor pr-10`}
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-primaryColor"
              >
                {!showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {errors.password && (
              <p className="text-xs text-red-500 mt-1">{errors.password}</p>
            )}
          </div>

          {/* Remember Me + Forgot Password */}
          <div className="flex items-center justify-between text-sm mt-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={() => setRememberMe(!rememberMe)}
                className="w-4 h-4"
              />
              Remember Me
            </label>

            <button
              onClick={() => navigate("/forgot-password")}
              className="flex items-center gap-1 text-secondaryTextColor hover:text-primaryColor"
            >
              <IoIosKey size={18} /> Forgot Password
            </button>
          </div>

          {/* Login Button */}
          <div>
            <button
              onClick={handleLogin}
              disabled={isLoading}
              className="w-full h-12 flex justify-center items-center bg-primaryBtnColor text-white font-semibold rounded-md transition hover:bg-primaryBtnHoverColor"
            >
              {isLoading ? <ButtonLoader /> : "Login"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
